/**
 * shareSheetData — what the exchange share sheet reads before it can say what
 * it offers (shareSheetRules; plan step 9): your groups with what each shares
 * and when you last used it, and your last Custom set (rule 18). All your own
 * rows (owner-only), all on the server, so a second device sees the same sheet.
 *
 * NEW_EXPERIENCE only: group_share_sets / groups.last_used_at /
 * share_last_custom exist on dev until the flip (046, 049), so this is never
 * called with the flag off.
 *
 * Permanent data layer: no React.
 */
import { supabase } from '../supabase';
import { isNewExperience } from '../../utils/newExperience';
import { logConnectionError } from '../analytics/eventLogger';

/**
 * @param {string} profileUserId
 * @returns {Promise<{groups: Array<{id, name, sortOrder, lastUsedAt, items: string[]}>,
 *   lastCustom: string[]|null}|null>} null = not known
 *
 * No special fallback (Mark, 2026-10-20; supersedes that morning's option A
 * retry): the sheet shows `Full profile` · `Custom` at once, and your groups /
 * last Custom set appear only if and when this answer arrives. A failed read
 * goes through the app's normal error tracking — the catalogued `database`
 * error (error-system.md; ERROR_CATALOG `error_database`), as other Supabase
 * reads and writes report (activeSession.js) — and the answer stays unknown.
 */
export const loadShareSheetData = async (profileUserId) => {
  if (!isNewExperience() || !profileUserId) return null;
  try {
    const [groupsRes, lastRes] = await Promise.all([
      supabase
        .from('groups')
        .select('id, name, sort_order, last_used_at')
        .eq('profile_user_id', profileUserId),
      supabase
        .from('share_last_custom')
        .select('items')
        .eq('owner_profile_user_id', profileUserId)
        .maybeSingle(),
    ]);
    if (groupsRes.error) throw groupsRes.error;
    if (lastRes.error) throw lastRes.error;
    const rows = groupsRes.data || [];
    let sets = [];
    if (rows.length > 0) {
      const setsRes = await supabase
        .from('group_share_sets')
        .select('group_id, item_key')
        .in(
          'group_id',
          rows.map((g) => g.id)
        );
      if (setsRes.error) throw setsRes.error;
      sets = setsRes.data || [];
    }
    const itemsOf = (id) => sets.filter((s) => s.group_id === id).map((s) => s.item_key);
    return {
      groups: rows.map((g) => ({
        id: g.id,
        name: g.name,
        sortOrder: g.sort_order ?? 0,
        lastUsedAt: g.last_used_at || null,
        items: itemsOf(g.id),
      })),
      lastCustom: lastRes.data?.items || null,
    };
  } catch (err) {
    logConnectionError('database', {
      operation: 'share_sheet_data_read',
      code: err?.code,
      message: err?.message || String(err),
      profileUserId,
    });
    return null;
  }
};

/**
 * What a repeat's sheet reads about THIS person (rule 7, plan step 13): your
 * own picks for them (connection_item_shares — Full profile is its reserved
 * row) and which of your groups you filed them in. shareSheetRules.receivedFrom
 * turns these, with the groups' share sets from loadShareSheetData, into "what
 * they already receive" — whether `Share more` shows, and what Custom starts
 * with ticked from it. Owner-only rows, so a second device agrees.
 *
 * NEW_EXPERIENCE only (049's rows are dev-only until the flip).
 *
 * @param {string} profileUserId
 * @param {string} room - this person's chat room
 * @returns {Promise<{personal: Object<string,('on'|'off')>, groupIds: string[]}|null>}
 *   null = not known. No special fallback (Mark, 2026-10-20): the sheet shows
 *   `Reconnect` at once and `Share more` only if and when this answer arrives,
 *   so an unknown answer simply leaves `Reconnect` alone. A failed read goes
 *   through the app's normal error tracking — the catalogued `database` error
 *   (error-system.md; ERROR_CATALOG `error_database`), as other Supabase reads
 *   and writes report (activeSession.js).
 */
export const loadReceivedData = async (profileUserId, room) => {
  if (!isNewExperience() || !profileUserId || !room) return null;
  try {
    const [picksRes, viewRes] = await Promise.all([
      supabase
        .from('connection_item_shares')
        .select('item_key, state')
        .eq('owner_profile_user_id', profileUserId)
        .eq('chat_room_id', room),
      supabase
        .from('connection_views')
        .select('id, connection_groups(group_id)')
        .eq('user_id', profileUserId)
        .eq('chat_room_id', room)
        .maybeSingle(),
    ]);
    if (picksRes.error) throw picksRes.error;
    if (viewRes.error) throw viewRes.error;
    return {
      personal: Object.fromEntries((picksRes.data || []).map((r) => [r.item_key, r.state])),
      groupIds: (viewRes.data?.connection_groups || []).map((g) => g.group_id),
    };
  } catch (err) {
    logConnectionError('database', {
      operation: 'share_sheet_received_read',
      code: err?.code,
      message: err?.message || String(err),
      profileUserId,
      chatRoomId: room,
    });
    return null;
  }
};
